import { Observable } from 'rxjs';
import { Injectable } from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

export class BusyTracker {
    private _pendingCount = 0;

    public constructor(public name?: string) {}

    public get isBusy() {
        return this._pendingCount > 0;
    }

    public start() {
        this._pendingCount++;
    }

    public end() {
        this._pendingCount--;
    }
}

@Injectable({
    providedIn: 'root'
})
export class BusyTrackerFactory {
    /**
     * creates a new BusyTracker instance
     */
    public create(name?: string): BusyTracker {
        return new BusyTracker(name);
    }
}

declare global {
    interface Promise<T> {
        /**
         * With the provided tracker, calls `start` now, and `end` when the promise completes
         * (regardless of whether or not it completes successfully).
         * @param tracker the BusyTracker instance to call
         * @returns this Promise
         */
        track(tracker: BusyTracker): Promise<T>;
        finally(fn: (x: any) => any): Promise<T>;
    }
}

/**
 * With the provided tracker, calls `start` now, and `end` when the promise completes
 * (regardless of whether or not it completes successfully).
 * @param promise the Promise instance to track
 * @param tracker the BusyTracker instance to call
 * @returns the provided Promise
 */
export function trackPromise<T>(promise: Promise<T>, tracker: BusyTracker) {
    if (tracker) {
        tracker.start();
        promise.finally(() => {
            tracker.end();
        });
    }
    return promise;
}

/**
 * Adds the `track` function to the Promise prototype
 * @see trackPromise
 */
export function addTrackToPromisePrototype() {
    if (!Promise) {
        throw new Error('Promises are not supported in this browser.');
    }

    Object.defineProperty(Promise.prototype, 'track', {
        value: function track<T>(this: Promise<T>, tracker: BusyTracker): Promise<T> {
            return trackPromise(this, tracker);
        },
        writable: true,
        configurable: true
    });
}

/**
 * With the provided tracker, calls `start` now, and `end` when the observable completes
 * (regardless of whether or not it completes successfully).
 * This subscribes to the Observable, making it 'hot'.
 * @param observable the Observable instance to track
 * @param tracker the BusyTracker instance to call
 * @param until (optional) Observable to pipe to takeUntil (to unsubscribe on destroy)
 * @returns the provided Observable
 */
export function trackObservable<T, U>(
    observable: Observable<T>,
    tracker: BusyTracker,
    until?: Observable<U>
): Observable<T> {
    const subject: Subject<T> = new Subject<T>();
    tracker.start();
    (until ? observable.pipe(takeUntil(until)) : observable).subscribe(
        v => subject.next(v),
        e => subject.error(e),
        () => {
            subject.complete();
            tracker.end();
        }
    );
    return subject.asObservable();
}

declare module 'rxjs/internal/Observable' {
    // eslint-disable-next-line no-shadow
    interface Observable<T> {
        /**
         * With the provided tracker, calls `start` now, and `end` when the observable completes
         * (regardless of whether or not it completes successfully).
         * This subscribes to the Observable, making it 'hot'.
         * @param tracker the BusyTracker instance to call
         * @param until (optional) Observable to pipe to takeUntil (to unsubscribe on destroy)
         * @returns this Observable
         */
        track<U>(tracker: BusyTracker, until?: Observable<U>): Observable<T>;
    }
}

/**
 * Adds the `track` function to the Observable prototype
 * @see trackObservable
 */
export function addTrackToObservablePrototype() {
    Object.defineProperty(Observable.prototype, 'track', {
        value: function track<T, U>(this: Observable<T>, tracker: BusyTracker, until?: Observable<U>): Observable<T> {
            return trackObservable(this, tracker, until);
        },
        writable: true,
        configurable: true
    });
}
